import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Card,
} from '@crossnokaye/ux-common';

import { AgentStatusVariant } from '@/hooks/use-agent-status-display';

import offlineModeHero from './assets/offline-mode-hero.svg';

type FaqAnswer = {
  topAnswer: string;
  subAnswers?: string[];
};

type FaqItem = {
  value: string;
  question: string;
  answers: FaqAnswer[];
};

type FaqSection = {
  title: string;
  items: FaqItem[];
};

type SplashContent = {
  description: string;
  faqSections?: FaqSection[];
  illustrationAlt?: string;
  illustrationSrc?: string;
  secondaryHeading?: string;
  secondaryBody?: string;
  subtitle: string;
  titleAccent: string;
  titlePrefix: string;
};

const offlineFaqSections: FaqSection[] = [
  {
    title: 'Offline Mode',
    items: [
      {
        value: 'what-is-offline-mode',
        question: 'What is Offline Mode and why is my facility in it?',
        answers: [
          {
            topAnswer:
              'The ATLAS Control System enters Offline Mode when it is unable to connect to the internet.',
          },
        ],
      },
      {
        value: 'how-do-i-know',
        question: 'How do I know when my facility is in Offline Mode?',
        answers: [
          {
            topAnswer:
              "On remote devices that are not connected to the facility's local network (such as internet-connected computers, tablets and mobile phones) the affected facility screen will be replaced with the Offline Mode screen when ATLAS is unable to connect to the internet. Remote interactions with the facility are not supported during Offline Mode. You must use the on-site Local Dashboard to monitor and control your facility.",
          },
          {
            topAnswer:
              'On the Local Dashboard, a yellow banner at the top of the ATLAS page indicates the facility is in Offline Mode and that control is limited to: Equipment Browser with the ability to change setpoints and modes, and Alarms with the ability to view alarms. Acknowledging and clearing Alarms are currently unavailable. For facilities with Defrost Scheduling, this feature is available in the Equipment Browser.',
          },
        ],
      },
      {
        value: 'what-is-local-dashboard',
        question: 'What is the Local Dashboard?',
        answers: [
          {
            topAnswer:
              'The Local Dashboard is the on-site device that is hardwired into the local network.',
            subAnswers: [
              "It only provides access to the facility the Agent is connected to. To access other facilities, use a remote device that is not connected to the facility's local network (such as internet-connected computers, tablets, and mobile phones).",
              'It is the only device that supports Offline Mode for maintaining safe operation when ATLAS is unable to connect to the internet.',
            ],
          },
          {
            topAnswer:
              'The location of your Local Dashboard and how to access it are documented in the ATLAS Site Reference manual provided by CrossnoKaye during the installation process.',
          },
          {
            topAnswer:
              'When internet connectivity is available, the Local Dashboard offers the full ATLAS experience for the current facility.',
          },
          {
            topAnswer:
              'When not connected to the internet, the Local Dashboard is limited to: Equipment Browser with the ability to change setpoints and modes, and Alarms with the ability to view alarms. Acknowledging and clearing Alarms are currently unavailable. For facilities with Defrost Scheduling, this feature is available in the Equipment Browser.',
          },
        ],
      },
      {
        value: 'monitor-and-control',
        question: 'How do I monitor and control my facility during Offline Mode?',
        answers: [
          {
            topAnswer:
              'Facilities in Offline Mode can only be monitored and controlled through the on-site HMI/Local Dashboard.',
          },
          {
            topAnswer:
              'The temperature and pressure commands that were issued before the facility entered Offline Mode are displayed in the Equipment Browser of the on-site Local Dashboard. Any changes made in the Local Dashboard are stored on the Agent and synced to the cloud once the internet is restored.',
          },
          {
            topAnswer:
              'The Local Dashboard is limited to: Equipment Browser with the ability to change setpoints and modes, and Alarms with the ability to view alarms. Acknowledging and clearing Alarms are currently unavailable. For facilities with Defrost Scheduling, this feature is available in the Equipment Browser.',
          },
        ],
      },
      {
        value: 'safety-systems-and-alarms',
        question: 'Are safety systems and alarms still in place during Offline Mode?',
        answers: [
          {
            topAnswer:
              'Yes. Safety systems, including NH3 and emergency shutdown, function as long as the facility has power.',
          },
          {
            topAnswer: 'Alarms operate as follows during Offline Mode:',
            subAnswers: [
              'Alarms are generated and display on the on-site Local Dashboard.',
              'Alarms are not displayed on remote devices that connect to ATLAS through the internet (such as computers, tablets, and mobile phones).',
            ],
          },
          {
            topAnswer:
              'ATLAS is unable to send SMS or email alarm notifications while in Offline Mode. Notifications resume once normal ATLAS operation is restored.',
          },
        ],
      },
      {
        value: 'multiple-facilities',
        question:
          'I have access to multiple facilities. Can I access any facilities when one is in Offline Mode?',
        answers: [
          {
            topAnswer:
              "Yes, you can always use a remote device that is not connected to the facility's local network (such as internet-connected computers, tablets, and mobile phones) to access other facilities that are online. When any facility is in Offline Mode, a badge next to your company name in the left pane shows the number of facilities in Offline Mode. In addition, the Offline Mode icon (cloud with strikethrough) displays next to the facility name when it is in Offline Mode.",
          },
          {
            topAnswer: 'The on-site Local Dashboard can only access its own facility.',
          },
        ],
      },
    ],
  },
  {
    title: 'Troubleshooting',
    items: [
      {
        value: 'resolve-connectivity',
        question: 'How can I resolve the connectivity issue?',
        answers: [
          {
            topAnswer:
              "If the problem does not resolve within a few minutes, contact your facility's IT support team and ensure they are working to fix the problem.",
          },
        ],
      },
      {
        value: 'when-is-it-fixed',
        question: 'How will I know when the problem is fixed?',
        answers: [
          {
            topAnswer:
              'When connectivity is restored, remote users will stop seeing this Offline Mode screen and will regain the normal ATLAS display with cloud-based monitoring and control.',
          },
          {
            topAnswer:
              'When internet connectivity is restored, the yellow Offline Mode banner on the Local Dashboard turns green. The Local Dashboard remains in Offline Mode until the Reconnect button in the green banner is clicked. Staying offline minimizes interruptions during intermittent internet connectivity.',
          },
        ],
      },
    ],
  },
  {
    title: 'ATLAS Operations',
    items: [
      {
        value: 'commands-executing',
        question: 'Is ATLAS executing the commands that I gave it?',
        answers: [
          {
            topAnswer:
              'The facility will execute any commands that ATLAS received up until the time it went offline, which is displayed at the top of this screen. During Offline Mode, you must use the on-site Local Dashboard to monitor and control your facility.',
          },
        ],
      },
      {
        value: 'lose-data',
        question: 'Will I lose my Equipment and Trending data during Offline Mode?',
        answers: [
          {
            topAnswer:
              'Equipment and Trending data is stored onsite during Offline Mode. All on-site data is sent to the cloud when the ATLAS Control System reconnects to the internet.',
          },
        ],
      },
      {
        value: 'shutdowns-during-offline',
        question: 'What will happen to my ATLAS facility shutdowns during Offline Mode?',
        answers: [
          {
            topAnswer:
              'Automated Building Shutdowns (ABS) and Demand Response Curtailment: Shutdowns that were scheduled before ATLAS entered Offline Mode will still take place. If Offline Mode has been active for more than 24 hours, the facility could be at risk for missing future shutdowns. Scheduled shutdowns cannot be canceled during Offline Mode.',
          },
          {
            topAnswer:
              'Building Shutdown (that is, One Button Shutdown): This feature located in the Settings menu is not available in Offline Mode.',
          },
        ],
      },
      {
        value: 'energy-ai-savings',
        question: 'Will I still get Energy AI (EAI) savings during Offline Mode?',
        answers: [
          {
            topAnswer:
              'Yes, most EAI savings will be realized during short durations of Offline Mode. However, if ATLAS cannot connect to the internet for more than 24 hours, savings for the month may be lower than typical.',
          },
        ],
      },
    ],
  },
];

interface AgentStatusSplashProps {
  variant: AgentStatusVariant;
}

const splashContentByVariant: Record<AgentStatusVariant, SplashContent> = {
  offline: {
    description:
      'Cloud monitoring and control are unavailable while the facility agent is offline. Use the on-site HMI/Local Dashboard until connectivity is restored.',
    faqSections: offlineFaqSections,
    illustrationAlt: 'Offline mode control transition illustration',
    illustrationSrc: offlineModeHero,
    subtitle: 'has taken control',
    titleAccent: 'Offline Mode',
    titlePrefix: 'Facility',
  },
  unentitled: {
    description: 'The facility is not configured for cloud control.',
    secondaryBody:
      'Use the on-site HMI/Local Dashboard to monitor and control the facility. If remote access should be available, contact CrossnoKaye support to review entitlements for this site.',
    secondaryHeading: 'What you can do',
    subtitle: 'use the on-site HMI',
    titleAccent: 'Unavailable',
    titlePrefix: 'Cloud Control',
  },
};

const FaqAnswerGroup = ({ answer }: { answer: FaqAnswer }) => (
  <div className="space-y-3">
    <p>{answer.topAnswer}</p>
    {answer.subAnswers?.length ? (
      <ul className="ml-5 list-disc space-y-2">
        {answer.subAnswers.map((subAnswer) => (
          <li key={subAnswer}>{subAnswer}</li>
        ))}
      </ul>
    ) : null}
  </div>
);

const FaqSectionCard = ({ section }: { section: FaqSection }) => (
  <Card className="overflow-hidden border border-[#2b2b2b] bg-[#111111] text-white">
    <div className="border-b border-[#262626] px-6 py-4">
      <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#f7d146]">
        {section.title}
      </h3>
    </div>

    <Accordion type="multiple" defaultValue={[]}>
      {section.items.map((item) => (
        <AccordionItem
          key={item.value}
          value={item.value}
          className="border-b border-[#262626] last:border-b-0"
        >
          <AccordionTrigger
            title={item.question}
            variant="medium"
            className="px-6 py-5 text-left font-semibold text-white hover:bg-[#1a1a1a] data-[state=open]:bg-[#1a1a1a]"
          />
          <AccordionContent className="bg-[#161616] px-6 pb-5 pt-0">
            <div className="space-y-4 border-l border-[#3a3a3a] pl-4 text-sm leading-7 text-[#d0d0d0]">
              {item.answers.map((answer) => (
                <FaqAnswerGroup key={answer.topAnswer} answer={answer} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </Card>
);

export const AgentStatusSplash = ({ variant }: AgentStatusSplashProps) => {
  const content = splashContentByVariant[variant];

  return (
    <div
      className="h-full overflow-auto bg-[#0f0f0f] px-gutter-md py-gutter-lg text-white"
      data-testid="agent-status-splash-page"
    >
      <div className="mx-auto flex max-w-[72rem] flex-col gap-gutter-md">
        <Card className="overflow-hidden bg-[#111111] text-white">
          <div className="flex flex-col gap-6 p-6 sm:p-8 lg:p-10">
            {content.illustrationSrc ? (
              <div className="mx-auto w-full max-w-[40rem]">
                <img
                  src={content.illustrationSrc}
                  alt={content.illustrationAlt}
                  className="h-auto w-full"
                />
              </div>
            ) : null}

            <div className="flex flex-col gap-4">
              <div className="space-y-2">
                <h1 className="text-4xl font-bold leading-none tracking-tight text-white sm:text-5xl">
                  {content.titlePrefix}{' '}
                  <span className="text-[#f7d146]">{content.titleAccent}</span>
                </h1>
                <p className="text-4xl font-bold leading-none tracking-tight text-[#8d8d8d] sm:text-5xl">
                  {content.subtitle}
                </p>
              </div>

              <p className="max-w-[42rem] text-base leading-7 text-[#d0d0d0] sm:text-lg">
                {content.description}
              </p>
            </div>
          </div>
        </Card>

        {content.faqSections?.length ? (
          <div className="flex flex-col gap-4">
            <div className="px-1">
              <h2 className="text-lg font-bold text-white sm:text-xl">
                Frequently Asked Questions
              </h2>
            </div>

            {content.faqSections.map((section) => (
              <FaqSectionCard key={section.title} section={section} />
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden border border-[#2b2b2b] bg-[#111111] text-white">
            <div className="flex flex-col gap-3 p-6 sm:p-8">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-[#f7d146]">
                {content.secondaryHeading}
              </h2>
              <p className="max-w-[42rem] text-base leading-7 text-[#d0d0d0]">
                {content.secondaryBody}
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
